// Version 1 is a persisted backup-file format.
import React, {useRef, useState} from "react";
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Stack,
    Typography,
} from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import {useTranslation} from "react-i18next";
import type {Editor, JSONContent} from "@tiptap/core";
import {setPrintoutEditorDocument} from "./loadPrintoutEditorDocument";

const PRINTOUT_BACKUP_TYPE = "crqlar-printout-backup";
const PRINTOUT_BACKUP_FILE_EXTENSION = ".crqlar-printout.json";

interface PrintoutBackupFile {
    content: JSONContent;
    createdAt: string;
    type: typeof PRINTOUT_BACKUP_TYPE;
    version: 1;
}

interface Props {
    editor: Editor | null;
    language?: string;
}

const replaceWithBackupContent = (editor: Editor, content: JSONContent) => {
    setPrintoutEditorDocument(editor, content);
    editor.commands.focus("end");
};

const isRecord = (value: unknown): value is Record<string, unknown> => (
    typeof value === "object" && value !== null && !Array.isArray(value)
);

const isTiptapDocumentContent = (value: unknown): value is JSONContent => (
    isRecord(value)
    && value.type === "doc"
    && (!("content" in value) || Array.isArray(value.content))
);

const parsePrintoutBackupContent = (value: unknown): JSONContent | null => {
    if (isRecord(value) && value.type === PRINTOUT_BACKUP_TYPE) {
        return isTiptapDocumentContent(value.content) ? value.content : null;
    }

    return isTiptapDocumentContent(value) ? value : null;
};

const backupContentMatchesSchema = (editor: Editor, content: JSONContent) => {
    try {
        editor.schema.nodeFromJSON(content);
        return true;
    } catch (error) {
        return false;
    }
};

const getBackupFileName = (language: string) => {
    const timestamp = new Date().toISOString().slice(0, 19).replace(/[T:]/g, "-");

    return `crqlar-printout-${language}-${timestamp}${PRINTOUT_BACKUP_FILE_EXTENSION}`;
};

const downloadJsonFile = (fileName: string, value: unknown) => {
    const blob = new Blob([JSON.stringify(value, null, 2)], {type: "application/json"});
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
};

const PrintoutBackupMenu = ({editor, language}: Props) => {

    const {t} = useTranslation();
    const backupInputRef = useRef<HTMLInputElement | null>(null);
    const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
    const [importDialogOpen, setImportDialogOpen] = useState(false);
    const [importError, setImportError] = useState<string | null>(null);
    const [pendingBackupContent, setPendingBackupContent] = useState<JSONContent | null>(null);

    const closeImportDialog = () => {
        setImportError(null);
        setPendingBackupContent(null);
        setImportDialogOpen(false);
    };

    const exportBackup = () => {
        setMenuAnchorEl(null);

        if (!editor || !language) return;

        const backup: PrintoutBackupFile = {
            type: PRINTOUT_BACKUP_TYPE,
            version: 1,
            createdAt: new Date().toISOString(),
            content: editor.getJSON(),
        };

        setImportError(null);
        downloadJsonFile(getBackupFileName(language), backup);
    };

    const openImportDialog = () => {
        setMenuAnchorEl(null);
        setImportError(null);
        setPendingBackupContent(null);
        setImportDialogOpen(true);
    };

    const requestBackupImport = () => {
        setImportError(null);
        backupInputRef.current?.click();
    };

    const handleBackupFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.currentTarget.files?.[0];
        event.currentTarget.value = "";

        if (!editor || !file) return;

        try {
            const parsed = JSON.parse(await file.text());
            const content = parsePrintoutBackupContent(parsed);

            if (!content || !backupContentMatchesSchema(editor, content)) {
                setImportError(t("This is not a valid printout backup."));
                return;
            }

            setImportError(null);

            if (editor.isEmpty) {
                replaceWithBackupContent(editor, content);
                closeImportDialog();
                return;
            }

            setPendingBackupContent(content);
        } catch (error) {
            setImportError(t("This is not a valid printout backup."));
        }
    };

    const restorePendingBackup = () => {
        if (!editor || !pendingBackupContent) return;

        replaceWithBackupContent(editor, pendingBackupContent);
        closeImportDialog();
    };

    return (
        <>
            <IconButton
                aria-label={t("More")}
                disabled={!editor}
                onClick={event => setMenuAnchorEl(event.currentTarget)}
            >
                <MoreVertIcon/>
            </IconButton>

            <Menu
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                onClose={() => setMenuAnchorEl(null)}
            >
                <MenuItem disabled={!editor} onClick={exportBackup}>
                    <ListItemIcon>
                        <FileDownloadOutlinedIcon fontSize={"small"}/>
                    </ListItemIcon>
                    <ListItemText>{t("Export layout backup")}</ListItemText>
                </MenuItem>

                <MenuItem disabled={!editor} onClick={openImportDialog}>
                    <ListItemIcon>
                        <UploadFileOutlinedIcon fontSize={"small"}/>
                    </ListItemIcon>
                    <ListItemText>{t("Import into this layout...")}</ListItemText>
                </MenuItem>
            </Menu>

            <input
                accept=".json,.crqlar-printout.json,application/json"
                hidden
                onChange={handleBackupFileChange}
                ref={backupInputRef}
                type="file"
            />

            {/* Empty documents skip the destructive restore confirmation. */}
            <Dialog
                fullWidth
                maxWidth={"sm"}
                onClose={pendingBackupContent ? () => setPendingBackupContent(null) : closeImportDialog}
                open={importDialogOpen}
            >
                <DialogTitle>
                    {pendingBackupContent ? t("Restore backup") : t("Import backup")}
                </DialogTitle>
                <DialogContent dividers={!pendingBackupContent}>
                    {pendingBackupContent ? (
                        <Typography color={"text.secondary"} fontSize={14}>
                            {t("This document already has content. Restoring a backup will replace the existing content.")}
                        </Typography>
                    ) : (
                        <Stack gap={1}>
                            <Typography color={"text.secondary"} fontSize={12}>
                                {t("Restore the active language layout from a local JSON backup file.")}
                            </Typography>
                            <Stack direction={"row"} flexWrap={"wrap"} gap={1}>
                                <Button
                                    disabled={!editor}
                                    onClick={requestBackupImport}
                                    startIcon={<UploadFileOutlinedIcon fontSize={"small"}/>}
                                    variant={"outlined"}
                                >
                                    {t("Choose file")}
                                </Button>
                            </Stack>
                            {importError && (
                                <Alert severity={"error"} sx={{py: 0}}>
                                    {importError}
                                </Alert>
                            )}
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    {pendingBackupContent ? (
                        <>
                            <Button onClick={() => setPendingBackupContent(null)}>{t("Back")}</Button>
                            <Button color={"error"} onClick={restorePendingBackup} variant={"contained"}>
                                {t("Restore backup")}
                            </Button>
                        </>
                    ) : (
                        <Button onClick={closeImportDialog}>{t("Cancel")}</Button>
                    )}
                </DialogActions>
            </Dialog>
        </>
    );
};

export default PrintoutBackupMenu;
